import type {ReactElement} from 'react';
import React, {useRef, useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {ClickableDiv} from '@Components/clickable-div';
import {InputField} from '@Components/input-field-v2';
import type {IconsOnlyControl, InputFieldControls} from '@Components/input-field-v2/input-field.types';
import {InputControlItemType, InputFieldSize, InputFieldState, InputFieldType} from '@Components/input-field-v2/input-field.types';
import useOutsideClick from '@Hooks/useOutsideClick';
import {useAppDispatch} from '@/hooks';
import {onEventWizardRegistrationChange} from '@Components/event-wizard/event-wizard-slice';
import {useEventWizardRegistrationDetails} from '@Components/event-wizard/event-wizard.hooks';
import {toggleDisplayPreviewRegistrationForm} from '@Components/event-wizard/preview/preview.helper';
import {isValidURL2} from '@Utils/url.util';

export function EventWizardRegistrationsButtonLink(): ReactElement {
  const dispatch = useAppDispatch();
  const registrationDetails = useEventWizardRegistrationDetails();

  const [isCustomLinkOptionOpen, setCustomLinkOptionOpen] = useState<boolean>(!!registrationDetails.registerBtnLink);
  const [buttonLink, setButtonLink] = useState<string>(registrationDetails.registerBtnLink ?? '');
  const [isLinkAdded, setIsLinkAdded] = useState<boolean>(!!registrationDetails.registerBtnLink);
  const [isInErrState, setIsInErrState] = useState<boolean>(false);

  const inputFieldRef = useRef<HTMLInputElement>(null);
  const selectorContainerRef = useRef<HTMLDivElement>(null);

  useOutsideClick(
    inputFieldRef,
    () => {
      if (buttonLink === '') {
        updateCustomLinkOptionVisibility(false);
      }
    },
    selectorContainerRef?.current
  );

  const updateCustomLinkOptionVisibility = (shouldDisplay: boolean): void => {
    setCustomLinkOptionOpen(shouldDisplay);
    toggleDisplayPreviewRegistrationForm(!shouldDisplay);

    if (!shouldDisplay) {
      saveLink('');
    }
  };

  const onDeleteLink = (): void => {
    setButtonLink('');
    updateCustomLinkOptionVisibility(false);
    setIsLinkAdded(false);
    saveLink('');
  };

  const onBlur = (): void => {
    setIsInErrState(!isValidURL2(buttonLink));
  }

  const onUpdateLink = (): void => {
    if (isValidURL2(buttonLink)) {
      setIsLinkAdded(buttonLink !== '');
      saveLink(buttonLink);
      return;
    }
    setIsInErrState(true);
  };

  const saveLink = (text: string): void => {
    dispatch(
      onEventWizardRegistrationChange({
        ...registrationDetails,
        registerBtnLink: !text ? undefined : text,
      })
    );
  };

  const onTypeLink = (text: string): void => {
    setIsInErrState(false);
    setButtonLink(text);

    if (text === '' || text !== registrationDetails.registerBtnLink) {
      setIsLinkAdded(false);
      return;
    }

    if (text === registrationDetails.registerBtnLink) {
      setIsLinkAdded(true);
    }
  };

  const getDefaultView = (): ReactElement => {
    return (
      <ClickableDiv
        ref={selectorContainerRef}
        onClick={() => {
          updateCustomLinkOptionVisibility(true);
        }}
        className="flex-row-align-center _cursor-pointer"
      >
        <Icon icon="icon-link" size={IconSize.SIZE_ICON_16} className="content-primary spacing-m-r-1" />
        <Text className="content-primary" val={window.i18next.t('pmwjs_add_custom_link_to_button')} size={TextSize.XXSMALL} bold />
      </ClickableDiv>
    );
  };

  const getLinkInputView = (): ReactElement => {
    return (
      <div className="flex-row-align-center align-items-start justify-content-center">
        <div className="flex-row-align-center spacing-m-t-2 spacing-m-r-10 _text-nowrap">
          <Icon icon="icon-link" size={IconSize.SIZE_ICON_16} className="spacing-m-r-1" />
          <Text val={window.i18next.t('pmwjs_button_link')} size={TextSize.XXSMALL} bold />
        </div>
        <div className="_full-width">
          <InputField
            ref={inputFieldRef}
            input={buttonLink}
            inputType={InputFieldType.TEXT}
            onInputChange={onTypeLink}
            onInputBlur={onBlur}
            size={InputFieldSize.XSMALL}
            state={isInErrState ? InputFieldState.ERROR : InputFieldState.DEFAULT}
            information={isInErrState ? window.i18next.t('pmwjs_enter_a_valid_link') : undefined}
            placeholder={window.i18next.t('pmwjs_add_custom_link_to_button')}
            controls={getInputControls()}
            onInputSubmit={() => {
              onUpdateLink();
            }}
          />
        </div>
      </div>
    );
  };

  const getInputControls = (): InputFieldControls => {
    const controlIcon: IconsOnlyControl = {
      type: InputControlItemType.ICONS_ONLY,
      id: isLinkAdded ? 'icon-delete' : 'icon-check',
      icon: isLinkAdded ? 'icon-delete' : 'icon-check',
      className: isLinkAdded ? 'content-danger' : '',
    };
    return {
      showDefaultControls: false,
      customControls: [controlIcon],
      onClick(iconName: string): void {
        if (iconName === 'icon-delete') {
          onDeleteLink();
        } else {
          onUpdateLink();
        }
      },
    };
  };

  return isCustomLinkOptionOpen ? getLinkInputView() : getDefaultView();
}
